import { AiModelPrice, AiPricesService } from '../service/ai/ai-prices.service';

// The AI Cost Calculator's arithmetic and questions (owner 2026-10-19), shared by the calculator card
// (component/ai-cost-widget), the calculator page's table and the home page's tools box.

/** one model with what the visitor's traffic costs on it */
export interface AiCostRow {
  model: AiModelPrice;
  perRequest: number;
  perDay: number;
  perMonth: number;
  /** requests a day the budget buys */
  withinBudget: number;
}

export type AiCostKey = 'requests' | 'input' | 'output' | 'cached' | 'days' | 'budget';

/** the calculator's numbers: input and output in tokens (asked in words), cached in %, budget in USD */
export type AiCostValues = { [ key in AiCostKey ]: number };

/** a question, with its url key and its bounds */
export interface AiCostField {
  key: AiCostKey;
  param: string;
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  /** asked in words, kept in tokens */
  words?: boolean;
  /** under More Options */
  more?: boolean;
}

/** a ready-made use: what it fills in and what one request is called on the card */
export interface AiCostScenario {
  key: string;
  label: string;
  /** one request, singular and plural: "chat message" / "chat messages" */
  one: string;
  many: string;
  values?: { requests: number; input: number; output: number; cached: number };
}

/** each platform's icon (assets/third-parties/ai, from Lobe Icons, MIT; OpenAI and xAI recoloured a neutral grey that
 *  reads on the light and the dark look) */
export function aiPlatformIcon( platform: string ): string {
  return `./assets/third-parties/ai/${ platform }.svg`;
}

/** English runs at about 750 words per thousand tokens */
export const WORDS_PER_TOKEN = 0.75;

export const AI_COST_SCENARIOS: AiCostScenario[] = [
  { key: 'chatbot', label: 'Chatbot', one: 'chat message', many: 'chat messages',
    values: { requests: 1000, input: 1500, output: 400, cached: 30 } },
  { key: 'coding', label: 'Coding Assistant', one: 'coding request', many: 'coding requests',
    values: { requests: 200, input: 20000, output: 1500, cached: 60 } },
  { key: 'summaries', label: 'Document Summaries', one: 'document', many: 'documents',
    values: { requests: 100, input: 6500, output: 500, cached: 0 } },
  { key: 'support', label: 'Customer Support', one: 'support reply', many: 'support replies',
    values: { requests: 500, input: 3000, output: 300, cached: 50 } },
  { key: 'custom', label: 'Something Else', one: 'request', many: 'requests' },
];

export const AI_COST_FIELDS: AiCostField[] = [
  { key: 'requests', param: 'r', label: 'How Many a Day', hint: 'Times your app asks the model', min: 1, max: 100000000, step: 1 },
  { key: 'input', param: 'in', label: 'Words Sent Each Time', hint: 'Your text, instructions and earlier messages', min: 1, max: 10000000, step: 1, words: true },
  { key: 'output', param: 'out', label: 'Words in Each Answer', hint: 'What the model writes back', min: 0, max: 10000000, step: 1, words: true },
  { key: 'cached', param: 'c', label: 'Repeated Text (%)', hint: 'Instructions sent every time, billed cheaper where the model caches them', min: 0, max: 100, step: 1, more: true },
  { key: 'days', param: 'd', label: 'Days a Month', hint: 'Days your app runs', min: 1, max: 31, step: 1, more: true },
  { key: 'budget', param: 'b', label: 'Monthly Budget (USD)', hint: 'Adds a column with how many a day it pays for', min: 0, max: 100000000, step: 1, more: true },
];

export const AI_COST_DEFAULTS: AiCostValues = { requests: 1000, input: 1500, output: 400, cached: 30, days: 30, budget: 0 };

export function aiCostScenario( key: string ): AiCostScenario {
  return AI_COST_SCENARIOS.find( sc => sc.key === key ) || AI_COST_SCENARIOS[ AI_COST_SCENARIOS.length - 1 ];
}

export function clampAiCost( field: AiCostField, n: number ): number {
  return Math.min( field.max, Math.max( field.min, Math.round( n ) ) );
}

/** every model priced at the given numbers, cheapest a month first */
export function priceAiCost( models: AiModelPrice[], v: AiCostValues ): AiCostRow[] {
  const cached = v.cached / 100;
  const rows = models.map( m => {
    const cachedPrice = m.cacheRead === null ? m.input : m.cacheRead;
    const perRequest = ( v.input * ( 1 - cached ) * m.input + v.input * cached * cachedPrice + v.output * m.output ) / 1000000;
    const perDay = perRequest * v.requests;
    const perMonth = perDay * v.days;
    const withinBudget = perRequest > 0 ? Math.floor( v.budget / ( perRequest * v.days ) ) : 0;
    return { model: m, perRequest, perDay, perMonth, withinBudget };
  } );
  return rows.sort( ( a, b ) => a.perMonth - b.perMonth || a.model.name.localeCompare( b.model.name ) );
}

/** "$12.40", "$0.0031": a bill, with the decimals a small one needs */
export function aiMoney( value: number ): string {
  if ( !isFinite( value ) ) { return ''; }
  if ( value >= 100 ) { return '$' + Math.round( value ).toLocaleString( 'en-US' ); }
  if ( value >= 1 ) { return '$' + value.toFixed( 2 ); }
  return AiPricesService.price( value );
}

/** the calculator page's url keys for the numbers, the use and the model, so a card can open the full tool on them */
export function aiCostQueryParams( v: AiCostValues, scenarioKey: string, modelId: string | null ): { [ key: string ]: string } {
  const params: { [ key: string ]: string } = { s: scenarioKey };
  AI_COST_FIELDS.forEach( f => ( params[ f.param ] = String( v[ f.key ] ) ) );
  if ( modelId ) { params.m = modelId; }
  return params;
}
